/**
 * Native replacements for the `fflate` APIs used in this app:
 * gzip / gunzip / strToU8 / strFromU8 / zip
 */

function asU8(input) {
  if (input == null) return new Uint8Array(0)
  if (input instanceof Uint8Array) return input
  if (ArrayBuffer.isView(input)) {
    return new Uint8Array(input.buffer, input.byteOffset, input.byteLength)
  }
  if (input instanceof ArrayBuffer) return new Uint8Array(input)
  if (typeof input === 'string') return new TextEncoder().encode(input)
  return Uint8Array.from(input)
}

export function strToU8(str) {
  return new TextEncoder().encode(String(str ?? ''))
}

export function strFromU8(u8) {
  const bytes = asU8(u8)
  if (typeof Buffer !== 'undefined') {
    return Buffer.from(bytes).toString('utf8')
  }
  return new TextDecoder().decode(bytes)
}

async function streamTransform(u8, TransformStreamCtor, format) {
  if (typeof TransformStreamCtor !== 'function') {
    throw new Error(`${format} is not supported in this environment`)
  }
  const stream = new TransformStreamCtor(format)
  const writer = stream.writable.getWriter()
  await writer.write(asU8(u8))
  await writer.close()
  const buffer = await new Response(stream.readable).arrayBuffer()
  return new Uint8Array(buffer)
}

export function gzip(input, opts, cb) {
  const callback = typeof opts === 'function' ? opts : cb
  const data = asU8(input)
  streamTransform(data, CompressionStream, 'gzip')
    .then((out) => callback?.(null, out))
    .catch((err) => callback?.(err))
}

export function gunzip(input, opts, cb) {
  const callback = typeof opts === 'function' ? opts : cb
  const data = asU8(input)
  streamTransform(data, DecompressionStream, 'gzip')
    .then((out) => callback?.(null, out))
    .catch((err) => callback?.(err))
}

/** CRC32 for ZIP local/central headers */
const CRC_TABLE = (() => {
  const table = new Uint32Array(256)
  for (let i = 0; i < 256; i += 1) {
    let c = i
    for (let k = 0; k < 8; k += 1) {
      c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1
    }
    table[i] = c >>> 0
  }
  return table
})()

function crc32(u8) {
  let c = 0xffffffff
  for (let i = 0; i < u8.length; i += 1) {
    c = CRC_TABLE[(c ^ u8[i]) & 0xff] ^ (c >>> 8)
  }
  return (c ^ 0xffffffff) >>> 0
}

function u16(n) {
  const b = new Uint8Array(2)
  b[0] = n & 0xff
  b[1] = (n >>> 8) & 0xff
  return b
}

function u32(n) {
  const b = new Uint8Array(4)
  b[0] = n & 0xff
  b[1] = (n >>> 8) & 0xff
  b[2] = (n >>> 16) & 0xff
  b[3] = (n >>> 24) & 0xff
  return b
}

function concatBytes(parts) {
  const len = parts.reduce((n, p) => n + p.length, 0)
  const out = new Uint8Array(len)
  let offset = 0
  for (const part of parts) {
    out.set(part, offset)
    offset += part.length
  }
  return out
}

async function deflateRaw(u8) {
  return streamTransform(u8, CompressionStream, 'deflate-raw')
}

/**
 * Callback-style ZIP builder matching fflate's `zip(files, opts, cb)`.
 * Uses deflate-raw when CompressionStream is available; otherwise store.
 */
export function zip(files, opts, cb) {
  const callback = typeof opts === 'function' ? opts : cb
  buildZip(files)
    .then((out) => callback?.(null, out))
    .catch((err) => callback?.(err))
}

async function buildZip(files) {
  const localParts = []
  const centralParts = []
  let offset = 0
  const names = Object.keys(files || {})

  for (const name of names) {
    const raw = files[name]
    const data = raw instanceof Uint8Array ? raw : strToU8(raw)
    const nameBytes = strToU8(name)
    const checksum = crc32(data)

    let compressed = data
    let method = 0 // store
    try {
      const deflated = await deflateRaw(data)
      if (deflated.length < data.length) {
        compressed = deflated
        method = 8 // deflate
      }
    } catch {
      // fall back to store
    }

    const localHeader = concatBytes([
      u32(0x04034b50),
      u16(20),
      u16(0),
      u16(method),
      u16(0),
      u16(0),
      u32(checksum),
      u32(compressed.length),
      u32(data.length),
      u16(nameBytes.length),
      u16(0),
      nameBytes,
    ])

    const localOffset = offset
    localParts.push(localHeader, compressed)
    offset += localHeader.length + compressed.length

    const central = concatBytes([
      u32(0x02014b50),
      u16(20),
      u16(20),
      u16(0),
      u16(method),
      u16(0),
      u16(0),
      u32(checksum),
      u32(compressed.length),
      u32(data.length),
      u16(nameBytes.length),
      u16(0),
      u16(0),
      u16(0),
      u16(0),
      u32(0),
      u32(localOffset),
      nameBytes,
    ])
    centralParts.push(central)
  }

  const centralDir = concatBytes(centralParts)
  const centralOffset = offset
  const end = concatBytes([
    u32(0x06054b50),
    u16(0),
    u16(0),
    u16(names.length),
    u16(names.length),
    u32(centralDir.length),
    u32(centralOffset),
    u16(0),
  ])

  return concatBytes([...localParts, centralDir, end])
}

export default { gzip, gunzip, zip, strToU8, strFromU8 }
